<template>
    <div>
        <div class="row align-items-center mb-4 gy-2 filtros-bloque">
            <div class="col-md-4">
                <input v-model="search" type="text" class="form-control form-control-sm" placeholder="Buscar..." />
            </div>
            <div class="col-md-3">
                <select v-model="sortKey" class="form-select form-select-sm">
                    <option value="tipo_biomasa">Ordenar por Tipo</option>
                    <option value="proyecto">Ordenar por Proyecto</option>
                    <option value="lote">Ordenar por Lote</option>
                    <option value="fecha_cosecha">Ordenar por Fecha</option>
                </select>
            </div>
            <div class="col-md-2">
                <button class="btn btn-outline-terra btn-sm w-100" @click="sortAsc = !sortAsc">
                    <font-awesome-icon :icon="sortAsc ? 'sort-amount-down' : 'sort-amount-up'" />
                    {{ sortAsc ? 'Asc' : 'Desc' }}
                </button>
            </div>
            <div class="col-md-3 text-end">
                <small class="text-muted">Página {{ page }} de {{ totalPages }}</small>
            </div>
        </div>

        <div v-if="loading" class="text-center py-5">
            <div class="spinner-border text-primary" role="status"></div>
        </div>

        <div v-else class="row gy-3">
            <div class="col-lg-4 col-md-6" v-for="b in paginatedData" :key="b.id">
                <BiomasaCard :biomasa="b" @marcar-lista="marcarComoLista" />
            </div>

            <div v-if="!filteredData.length" class="col-12 text-center py-5">
                <font-awesome-icon :icon="['fas', 'plant-wilt']" />
                <h5 class="text-secondary">Todavía no hay biomasa pendiente para mostrar.</h5>
                <p class="text-muted small">
                    Para generar biomasa, debés <strong>cosechar plantas</strong> desde un evento de cosecha en un
                    proyecto de cultivo.
                </p>
            </div>
        </div>

        <nav v-if="totalPages > 1" class="mt-4">
            <ul class="pagination justify-content-center">
                <li class="page-item" :class="{ disabled: page === 1 }">
                    <button class="page-link" @click="prevPage">Anterior</button>
                </li>
                <li v-for="n in visiblePages" :key="n" class="page-item" :class="{ active: page === n }">
                    <button class="page-link" @click="goTo(n)">{{ n }}</button>
                </li>
                <li class="page-item" :class="{ disabled: page === totalPages }">
                    <button class="page-link" @click="nextPage">Siguiente</button>
                </li>
            </ul>
        </nav>
    </div>
</template>

<script>
import axios from '@/axios';
import { mapGetters } from 'vuex';
import BiomasaCard from '@/components/Biomasa/BiomasaCard.vue';

export default {
    name: 'BiomasaPendiente',
    components: { BiomasaCard },
    data() {
        return {
            biomasaList: [],
            loading: false,
            search: '',
            sortKey: 'tipo_biomasa',
            sortAsc: true,
            page: 1,
            pageSize: 9
        };
    },
    computed: {
        ...mapGetters(['getClubActual']),
        club_id() { return this.getClubActual.id; },
        filteredData() {
            const term = this.search.toLowerCase();
            return this.biomasaList.filter(b => {
                const codigoBiomasa = (b.codigo || '').toLowerCase();
                const tipo = (b.tipo_biomasa || '').toLowerCase();
                const proyecto = (b.proyecto?.nombre || '').toLowerCase();
                const lote = (b.lote?.nombre || '').toLowerCase();
                const plantas = b.planta_biomasa.map(pb => pb.planta.codigo.toLowerCase()).join(' ');

                return (
                    codigoBiomasa.includes(term) ||
                    tipo.includes(term) ||
                    proyecto.includes(term) ||
                    lote.includes(term) ||
                    plantas.includes(term)
                );
            });
        }
        ,
        sortedData() {
            return this.filteredData.slice().sort((a, b) => {
                const va = a[this.sortKey] || '';
                const vb = b[this.sortKey] || '';
                return this.sortAsc ? (va < vb ? -1 : 1) : (va > vb ? -1 : 1);
            });
        },
        paginatedData() {
            const start = (this.page - 1) * this.pageSize;
            return this.sortedData.slice(start, start + this.pageSize);
        },
        totalPages() {
            return Math.ceil(this.sortedData.length / this.pageSize) || 1;
        },
        visiblePages() {
            const pages = [];
            const total = this.totalPages;
            let start = Math.max(1, this.page - 2);
            let end = Math.min(total, start + 4);
            if (end - start < 4) start = Math.max(1, end - 4);
            for (let i = start; i <= end; i++) pages.push(i);
            return pages;
        }
    },
    methods: {
        prevPage() { if (this.page > 1) this.page--; },
        nextPage() { if (this.page < this.totalPages) this.page++; },
        goTo(n) { this.page = n; },
        async fetch() {
            this.loading = true;
            this.page = 1;
            try {
                const res = await axios.get('/api/lista_biomasa_espera_materia_prima', {
                    params: { club_id: this.club_id }
                });
                this.biomasaList = res.data || [];
            } finally {
                this.loading = false;
            }
        },
        async marcarComoLista({ id, fecha }) {
            const confirmar = confirm(`¿Confirmar marcar #${id} como lista con fecha ${fecha}?`);
            if (!confirmar) return;

            await axios.put('/api/biomasa_actualizar_lista_produccion', {
                biomasa_ids: [id],
                fecha_lista_produccion: fecha,
                proyecto_evento_id: null
            }, { params: { club_id: this.club_id } });

            const biomasa = this.biomasaList.find(b => b.id === id);
            if (biomasa) biomasa.fecha_lista_produccion = fecha;
        }
    },
    mounted() {
        this.fetch();
    }
};
</script>
